import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { ModuleKey, PermissionLevel } from '@masamune/shared';

interface Props {
  onClose: () => void;
}

const MODULE_LABELS: Record<string, string> = {
  DASHBOARD: 'Dashboard',
  CRM: 'CRM',
  MARKETING: 'Digital Marketing',
  PRODUCT: 'Digital Product',
  DESIGN: 'Digital Design',
  TALENT: 'Talent Sourcing',
  SUPPLIERS: 'Professionisti',
  FATTURAZIONE: 'Fatturazione',
  SERVICES: 'Servizi gestiti',
  VAULT: 'Password Vault',
  REPORT: 'Report',
  SETTINGS: 'Impostazioni',
  MARGIN: 'Marginalità (gated)',
  TASKS: 'Task',
  AMMINISTRAZIONE: 'Amministrazione',
  TRATTATIVE: 'Trattative',
};

const COLORS = ['#050514', '#2550a0', '#a04f25', '#4b2570', '#1f6857', '#705525', '#555555', '#888888'];

export function NewRoleModal({ onClose }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [shortLabel, setShortLabel] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [canSeeMargin, setCanSeeMargin] = useState(false);
  const [permissions, setPermissions] = useState<Record<string, PermissionLevel>>(() => {
    const init: Record<string, PermissionLevel> = {};
    for (const m of Object.values(ModuleKey)) init[m] = PermissionLevel.NONE;
    init[ModuleKey.DASHBOARD] = PermissionLevel.READ; // default sensato
    return init;
  });

  const setPermLevel = (m: string, level: PermissionLevel) => {
    setPermissions((p) => ({ ...p, [m]: level }));
  };

  const create = useMutation({
    mutationFn: () => api.post('/roles', {
      name, color, shortLabel: shortLabel.toUpperCase().slice(0, 4), canSeeMargin,
      permissions: Object.entries(permissions)
        .filter(([, level]) => level !== PermissionLevel.NONE)
        .map(([module, level]) => ({ module, level })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      toast.ok('Ruolo creato', name);
      onClose();
    },
    onError: (err) => toast.alert('Errore creazione ruolo', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const levelColor: Record<PermissionLevel, string> = {
    NONE: 'var(--paper-2)',
    READ: 'oklch(0.55 0.14 250)',
    WRITE: 'oklch(0.64 0.14 155)',
    ADMIN: 'var(--ink)',
  };

  return (
    <Modal
      title="Nuovo ruolo"
      kicker="Settings · Ruoli & permessi"
      onClose={onClose}
      size="xl"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!name || !shortLabel || create.isPending}>
            Crea ruolo <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-section">
        <div className="form-section-hd">Identità</div>
        <div className="form-grid">
          <Field label="Nome ruolo" required span={6}>
            <input className="inp" placeholder="es. Marketing Lead" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Sigla (max 4 char)" required span={2}>
            <input className="inp mono uppercase" maxLength={4} value={shortLabel} onChange={(e) => setShortLabel(e.target.value.toUpperCase())} />
          </Field>
          <Field label="Colore" span={4}>
            <div className="flex gap-1.5 items-center h-[28px]">
              {COLORS.map((c) => (
                <button key={c}
                  onClick={() => setColor(c)}
                  className="w-6 h-6 rounded-full border-2 transition"
                  style={{ background: c, borderColor: color === c ? 'var(--accent)' : 'transparent', transform: color === c ? 'scale(1.15)' : undefined }}
                />
              ))}
            </div>
          </Field>
          <Field label="" span={12}>
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={canSeeMargin} onChange={(e) => setCanSeeMargin(e.target.checked)} />
              Può visualizzare i dati di marginalità (riservato Super Admin/CFO)
            </label>
          </Field>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Matrice permessi</div>
        <div className="mute text-[13px] mb-3">
          Per ogni modulo seleziona il livello: <span className="font-semibold text-text">NONE</span> · nessun accesso, <span className="font-semibold text-info">READ</span> · sola lettura, <span className="font-semibold text-ok">WRITE</span> · può creare/modificare, <span className="font-semibold">ADMIN</span> · pieno controllo (incluso permessi).
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Modulo</th>
              <th className="text-center">NONE</th>
              <th className="text-center">READ</th>
              <th className="text-center">WRITE</th>
              <th className="text-center">ADMIN</th>
            </tr>
          </thead>
          <tbody>
            {Object.values(ModuleKey).map((m) => (
              <tr key={m}>
                <td className="font-semibold">{MODULE_LABELS[m] ?? m}</td>
                {(['NONE', 'READ', 'WRITE', 'ADMIN'] as PermissionLevel[]).map((lvl) => {
                  const selected = permissions[m] === lvl;
                  return (
                    <td key={lvl} className="text-center">
                      <button
                        onClick={() => setPermLevel(m, lvl)}
                        className="w-6 h-6 rounded-full border-2 transition mx-auto"
                        style={{
                          background: selected ? levelColor[lvl] : 'transparent',
                          borderColor: selected ? levelColor[lvl] : 'var(--line-2)',
                        }}
                        title={lvl}
                      >
                        {selected && lvl !== 'NONE' && <Icon name="check" size={10} className="text-white" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
